export default {
  name: {
    id: 'rm_metal_graphite_name',
    defaultMessage: 'Graphite',
  },
  smartphoneLabel: {
    id: 'rm_metal_graphite_smartphone_label',
    defaultMessage: 'Several grams',
  },
  whatItDoesText: {
    id: 'rm_metal_graphite_what_it_does',
    defaultMessage:
      'The graphite electrode, the negative side of a lithium-ion battery, is where lithium ions are stored during charging and released during discharge. Without it, the battery cannot function. Lithium gets all the attention, but graphite is just as essential.',
  },
  extractionText1: {
    id: 'rm_metal_graphite_extraction',
    defaultMessage:
      'Natural graphite[^*] is mined through open-pit operations. The ore is crushed, ground, and separated through a water-based process to concentrate the graphite flakes. To produce battery-grade graphite, the flakes are milled into rounded particles and chemically purified using a highly corrosive acid or heat treatment.',
  },
  quickFactText: {
    id: 'rm_metal_graphite_quick_fact',
    defaultMessage:
      'Graphite mining generates major water and air pollution through mineral dust and chemical waste, affecting both miners and surrounding communities.',
  },
  envSummaryText: {
    id: 'rm_metal_graphite_env_summary',
    defaultMessage:
      'From dust and chemical waste that pollute air and water, to mining waste stored in reservoirs that risk collapse, graphite mining has significant environmental impacts. Open-pit mining generates mineral dust, a respiratory hazard at high concentrations. Converting raw flakes into battery-grade graphite requires purification with highly corrosive chemicals, generating toxic waste that can contaminate soil and water.',
  },
  envWaterText: {
    id: 'rm_metal_graphite_env_water_text',
    defaultMessage:
      'Graphite dust from open-pit mining and toxic wastewater from chemical purification contaminate surrounding soil, water and air. Mining waste stored in reservoirs poses an additional contamination risk to downstream communities if these storage facilities fail, as demonstrated by incidents at graphite mining operations in Cabo Delgado Province, Mozambique.',
  },
  envSoilText: {
    id: 'rm_metal_graphite_env_soil_text',
    defaultMessage:
      'Large volumes of mining waste and chemical residues from battery-grade graphite processing accumulate near operations and leach into surrounding soils. If these storage sites fail, surrounding agricultural land faces the risk of permanent contamination.',
  },
  envBiodiversityText: {
    id: 'rm_metal_graphite_env_biodiversity_text',
    defaultMessage:
      "In Mozambique's Cabo Delgado province, graphite operations are located near biologically diverse forest ecosystems. Mining-induced habitat clearance, dust pollution, and water contamination impact surrounding ecosystems. Globally, regulatory oversight of environmental assessments and land rehabilitation requirements obligations remains consistently inadequate",
  },
  envClimateText: {
    id: 'rm_metal_graphite_env_climate_text',
    defaultMessage:
      'No specific data documenting the climate impact of graphite mining has been found. Globally, mineral and metal production accounts for about 10% of total energy-related greenhouse gas emissions.',
  },
  humanSummaryText: {
    id: 'rm_metal_graphite_human_summary',
    defaultMessage:
      'Graphite mining concentrates human impacts in two main areas. In China, which produces about 80% of the world’s graphite, communities near mining operations face dust and chemical pollution. In Mozambique’s Cabo Delgado province, communities face forced resettlement, an insurgency active since 2017 and employment that largely goes to non-local workers. As production expands to meet battery demand, these human-rights concerns may grow.',
  },
  humanDisplacementText: {
    id: 'rm_metal_graphite_human_displacement_text',
    defaultMessage:
      'Graphite extraction is associated with involuntary resettlement and land loss. In Mozambique, legislation permits mining resettlement, raising consent concerns. In 2024, protests over unresolved resettlement at Balama mine forced a temporary closure and triggered defaults on US-backed loans. In Madagascar’s Androy region, a 2025 study reported households losing land, some without opportunities to negotiate or provide free and informed consent, while promised compensation was not always honoured.',
  },
  humanWaterText: {
    id: 'rm_metal_graphite_human_water_text',
    defaultMessage:
      "Chemical waste and graphite dust from mining operations contaminate water sources communities depend on for drinking and agriculture. In Mozambique's Cabo Delgado, water and air pollution from graphite operations have been documented, with affected communities having no formal recourse.",
  },
  humanHealthText: {
    id: 'rm_metal_graphite_human_health_text',
    defaultMessage:
      'Inhaling graphite dust over time causes irreversible lung damage, a condition comparable to what coal miners develop, that can lead to inflammation, fibrosis, and in severe cases, death. Both miners and communities living near operations are at risk. Health monitoring remains inadequate in all major producing regions.',
  },
  humanGenderText: {
    id: 'rm_metal_graphite_human_gender_text',
    defaultMessage:
      'In Madagascar’s Androy region, a 2025 study by Transparency International Initiative Madagascar reports allegations of sexual harassment, sexual assault and verbal abuse linked to a graphite-mining project. It also documents gender discrimination in recruitment, job allocation and access to higher-paying positions.',
  },
  humanLegalText: {
    id: 'rm_metal_graphite_human_legal_text',
    defaultMessage:
      'Since 2017, Mozambique has experienced political instability and a violent insurgency in Cabo Delgado, where most graphite mines are located. The province has the lowest education level in Mozambique and high unemployment, with regional asymmetries implicated in driving youth to join armed groups. Most workers at major graphite operations are not locally hired, a source of long-standing regional tension. Weak governance heightens the risk of human rights violations across the graphite sector.',
  },
  geoSummaryText: {
    id: 'rm_metal_graphite_geo_summary',
    defaultMessage:
      'China accounts for nearly 80% of global natural graphite mining and 96% of battery-grade refining. Even under optimistic scenarios, China’s share of battery-grade refining is projected to remain above 80% by 2040. Export restrictions introduced in December 2023 have shown how quickly this concentration can become a geopolitical lever.',
  },
  geoDugText: {
    id: 'rm_metal_graphite_geo_dug_text',
    defaultMessage:
      'China accounts for 79.1% of global natural graphite mining. The next three largest producers, Mozambique (6.4%), Brazil (4.3%) and Madagascar (4.1%), together contribute only around 15%. By 2030, diversification efforts are expected to reduce China’s share as African and Australian projects scale up, although refining will remain highly concentrated.',
  },
  geoProcessedText: {
    id: 'rm_metal_graphite_geo_processed_text',
    defaultMessage:
      'China refines approximately 96% of the world’s battery-grade graphite. The process that converts raw flakes into rounded particles suitable for battery anodes is one of the most geographically concentrated stages in the battery supply chain, with China overwhelmingly dominant. Much of the graphite mined in Mozambique or Australia is shipped to China for this processing. By 2035, China is projected to supply around 80% of battery-grade graphite despite diversification efforts.',
  },
  geoLeverageText: {
    id: 'rm_metal_graphite_geo_leverage_text',
    defaultMessage:
      'China introduced export licensing requirements on graphite in December 2023. In December 2024, it tightened controls specifically targeting US-bound exports, requiring permits renewed every six months. A 25% US tariff on Chinese graphite takes effect in 2026, turning graphite into an active front in the US-China trade war over battery supply chains.',
  },
  supplySummaryText: {
    id: 'rm_metal_graphite_supply_summary',
    defaultMessage:
      'Most commercial lithium-ion batteries use graphite anodes, and no substitute has displaced graphite at scale. Under baseline projections, global supply in 2035 would meet only 89% of projected demand. If China were removed from the supply chain, remaining supply would cover just 35% to 40% of that demand. Closing this gap would require years of new mining and processing capacity.',
  },
  supplyReservesText: {
    id: 'rm_metal_graphite_supply_reserves_text',
    defaultMessage:
      'China holds around 28% of global natural graphite reserves. The USGS estimates world reserves at 290 million tonnes and identified resources at more than 800 million tonnes. At 2024 production rates, reserves alone represent more than a century of supply. The geological resource is not the main constraint; the bottleneck is processing capacity.',
  },
  supplyDemandText: {
    id: 'rm_metal_graphite_supply_demand_text',
    defaultMessage:
      'Graphite demand is projected to grow from 5.8 Mt in 2025 to 9.9 Mt in 2050, an increase of about 71%. EVs and grid storage account for 43% of demand in 2025; their share rises to 54% in 2030, then falls to 37% in 2050. Over time, silicon, lithium metal and hard carbon may gain market share as alternatives to graphite.',
  },
  recyclingSummaryText: {
    id: 'rm_metal_graphite_recycling_summary',
    defaultMessage:
      'Virtually no recycled graphite from end-of-life batteries currently re-enters the market, and no graphite is recovered from smartphones. Recycling graphite is economically challenging because its market value is low compared with newly mined material. Battery recyclers therefore prioritise higher-value metals such as lithium, cobalt and nickel. Without a significant change in economics or policy, graphite recycling will not close the supply gap.',
  },
  recyclingInputText: {
    id: 'rm_metal_graphite_recycling_input_text',
    defaultMessage:
      'Approximately 2.7% of total graphite demand, or around 127,000 tonnes out of 4.8 million tonnes, came from recycled sources in 2024. Most of this came from manufacturing offcuts rather than end-of-life devices. Recycled volumes are projected to grow to 227,000 tonnes by 2040, but total demand is expected to grow faster, so the recycled share could decline further.',
  },
  recyclingOutlookText: {
    id: 'rm_metal_graphite_recycling_outlook_text',
    defaultMessage:
      'Not yet. Three obstacles remain. First, most electric vehicle batteries sold today will not reach end-of-life until the mid-2030s, limiting the material available to recyclers. Second, graphite’s low value makes recycling less competitive than primary production without policy support. Third, future batteries may use more silicon instead of graphite, reducing the long-term supply of graphite recycling feedstock. The gap remains large.',
  },
  footnote1Text1: {
    id: 'rm_metal_graphite_footnote_1_p1',
    defaultMessage:
      'Carbon (C) is the chemical element found in all organic matter. It takes many physical forms depending on how its atoms are arranged: diamond, coal, graphene, and graphite are all carbon. Graphite is the specific crystalline form of carbon used in battery anodes, where its layered structure allows lithium ions to move in and out during charging. Natural graphite is graphite extracted from geological deposits, as opposed to synthetic graphite, which is manufactured industrially from petroleum coke. Throughout this sheet, "graphite" refers to natural graphite unless otherwise specified.',
  },
  footnote2Text1: {
    id: 'rm_metal_graphite_footnote_2_p1',
    defaultMessage:
      'Graphite scores are estimated rather than published. As graphite is not covered by the ADEME 2024 study, its environmental, social, geopolitical and supply-risk scores were calculated using ADEME’s methodology, public sources and AI assistance. These scores carry greater uncertainty. See the Sources tab for the full methodology and references.',
  },
  mine0Region: {
    id: 'rm_metal_graphite_mine0_region',
    defaultMessage: 'Balama',
  },
  mine1Region: {
    id: 'rm_metal_graphite_mine1_region',
    defaultMessage: 'Luobei',
  },
  mine2Region: {
    id: 'rm_metal_graphite_mine2_region',
    defaultMessage: 'Ruangwa',
  },
  mine3Region: {
    id: 'rm_metal_graphite_mine3_region',
    defaultMessage: 'Fotadrevo',
  },
  mine4Region: {
    id: 'rm_metal_graphite_mine4_region',
    defaultMessage: 'Itabela',
  },
} as const
